import { differenceInCalendarDays, format } from 'date-fns';

/** Ember's `capitalize` helper — upper-cases the first character only. */
function upperFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Epoch seconds (the shape every delivery timestamp uses) or, for the in-app/inbox config
 * payloads, a raw string — numeric strings are epoch seconds like Ember's `moment.unix`,
 * anything else is left to `Date` to parse.
 */
export type TimestampInput = number | string;

function toDate(value: TimestampInput): Date | null {
  const date =
    typeof value === 'string'
      ? Number.isFinite(Number(value))
        ? new Date(Number(value) * 1000)
        : new Date(value)
      : new Date(value * 1000);
  // `date-fns` `format` throws on an Invalid Date, and the in-app/inbox `expiry` is a
  // free-form string off the delivery body — a malformed one must not take the panel down.
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Moment's calendar words for the days either side of today, or `null` when the date is
 * outside that window and the caller's own fallback format applies.
 */
function calendarDayWord(dayDiff: number): string | null {
  if (dayDiff === 0) return 'today';
  if (dayDiff === -1) return 'yesterday';
  if (dayDiff === 1) return 'tomorrow';
  return null;
}

/** The viewer's short time-zone name (`PDT`) — moment's `z` token. */
function timeZoneAbbreviation(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
}

/**
 * Ember `moment.shortCalendar` parity — the "Date Created" column and the metrics
 * timeline. A calendar-relative label with a lowercase meridiem for the surrounding
 * week ("Today at 3:14 pm", the weekday name for other days within a week), and a
 * plain "MMM D, YYYY" with no time for anything older or further out. Ember's
 * `FormatTimestamp` capitalizes the first character by default, so "today" reads
 * "Today"; pass `capitalize: false` for mid-sentence use. `now` is injectable for tests.
 */
export function formatShortCalendar(
  epochSeconds: TimestampInput,
  now: Date = new Date(),
  { capitalize = true }: { capitalize?: boolean } = {},
): string {
  const date = toDate(epochSeconds);
  if (date == null) return String(epochSeconds);
  const dayDiff = differenceInCalendarDays(date, now);

  // Beyond the surrounding week (moment's `sameElse`): date only, no time.
  if (dayDiff < -6 || dayDiff > 6) {
    return format(date, 'MMM d, yyyy');
  }

  const time = format(date, 'h:mm a').toLowerCase();
  const dayWord = calendarDayWord(dayDiff) ?? format(date, 'EEEE');
  const formatted = `${dayWord} at ${time}`;
  return capitalize ? upperFirst(formatted) : formatted;
}

/**
 * Ember `LongTimestamp` parity — the absolute timestamps (preview header, expanded-row
 * `Created`, engagement rows, config expiry, "Last updated"). Moment's default calendar
 * plus `outputFormat`: "Today at 11:47:49 am (PDT)" for yesterday/today/tomorrow,
 * otherwise "Jul 27, 2026 at 2:31:00 pm (PDT)". Capitalized by default, as
 * `FormatTimestamp` does; pass `capitalize: false` mid-sentence ("Last updated today at …").
 */
export function formatLongTimestamp(
  epochSeconds: TimestampInput,
  now: Date = new Date(),
  { capitalize = true }: { capitalize?: boolean } = {},
): string {
  const date = toDate(epochSeconds);
  if (date == null) return String(epochSeconds);
  const dayDiff = differenceInCalendarDays(date, now);
  const time = `${format(date, 'h:mm:ss a').toLowerCase()} (${timeZoneAbbreviation(date)})`;
  const dayWord = calendarDayWord(dayDiff) ?? format(date, 'MMM d, yyyy');
  const formatted = `${dayWord} at ${time}`;
  return capitalize ? upperFirst(formatted) : formatted;
}

/**
 * Ember's moment `defaultFormat` (`config/environment.js`) — the absolute form
 * every calendar timestamp falls back to, and the one its hover tooltip shows:
 * "Sep 16, 2026 at 3:51:14 pm (PDT)", whatever day it is.
 */
export function formatDefaultTimestamp(epochSeconds: TimestampInput): string {
  const date = toDate(epochSeconds);
  if (date == null) return String(epochSeconds);
  return `${format(date, 'MMM d, yyyy')} at ${format(date, 'h:mm:ss a').toLowerCase()} (${timeZoneAbbreviation(date)})`;
}
